import { TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useMemo } from "react";
import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { ChargesColors } from "@/pages/sy/theme/colors";

interface CommoditySelectorProps {
  value: number | undefined | null;
  onChange: (commodityId: number | null) => void;
  disabled?: boolean;
  onlyEnabled?: boolean;
  bgColor?: string;
  borderColor?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  excludedCommodityIds?: number[];
  disableClearable?: boolean;
}

export const CommoditySelector = ({
  value,
  onChange,
  disabled = false,
  onlyEnabled = true,
  bgColor = "white",
  borderColor = ChargesColors.BORDER_MEDIUM,
  onBlur = () => {},
  onFocus = () => {},
  excludedCommodityIds = [],
  disableClearable = false,
}: CommoditySelectorProps) => {
  const { data: commodities = [] } = useCommoditiesQuery();

  const enabledCommodities = useMemo(() => {
    return commodities.filter((c) => c.IsEnable);
  }, [commodities]);

  const selectedCommodity = value
    ? enabledCommodities.find((c) => c.CommodityId === value)
    : null;

  const options = (onlyEnabled ? enabledCommodities : commodities).filter(
    (c) =>
      c.CommodityId == null || !excludedCommodityIds.includes(c.CommodityId),
  );

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => {
        if (typeof option === "object" && option !== null) {
          return option.CommodityName || "";
        }
        return "";
      }}
      value={selectedCommodity}
      onChange={(_, newValue) => {
        onChange(newValue ? newValue.CommodityId : null);
      }}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      disableClearable={disableClearable}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          fullWidth
          placeholder={!selectedCommodity ? "Select a commodity..." : ""}
          required
          slotProps={{
            input: {
              ...params.InputProps,
              style: {
                backgroundColor: disabled ? bgColor : "white",
                borderColor: borderColor,
                height: "40px",
              },
            },
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              padding: "6px 14px 10px 14px",
            },
          }}
        />
      )}
    />
  );
};
